import React, { FC, useEffect, useState } from 'react';
import { Formik } from 'formik';
import { Form, FormItem, Input } from 'formik-antd';
import { object } from 'yup';
import { FormikOnSubmit } from 'lib/formik.utils';
import {
  requiredEmailValidationSchema,
  firstNameValidationSchema,
  lastNameValidationSchema,
  phoneNumberValidationSchema,
  phoneNumberPrefixCodeValidation,
  jobTitleValidationSchema,
} from 'common/config/validationSchema';
import { defaultFormLayout } from 'common/config/formLayout';
import { MedicalOperatorFormModel } from 'models/medicalOperator';
import { AdministrationGuard } from 'common/components/AdministrationGuard';
import { useRole } from 'common/hooks/useRole';
import { SaveButton } from 'common/components/SaveButton';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { SubpartnersRegisterSelect } from 'common/components/SubpartnersSelect';
import { Checkbox } from 'antd';
import { FormattedMessage } from 'react-intl';
import { useQuery } from 'react-query';
import { QueryKey } from 'api/queryKey';
import { BusinessPartnersAPI } from 'api/BusinessPartnersAPI';
import { isNil } from 'ramda';
import { BusinessPartnerContext } from 'pages/BusinessPartnerManagement/BusinessPartnerContext';
import { useEnsureContext } from 'common/hooks';

export interface BusinessPartnerRegisterMedicalOperatorFormProps {
  onSubmit: FormikOnSubmit<MedicalOperatorFormModel>;
  loading: boolean;
  initialValues: MedicalOperatorFormModel;
  checkedDenyAccess: boolean;
  setCheckedDenyAccess: React.Dispatch<React.SetStateAction<boolean>>;
  medicalOperatorId: number;
}

export const BusinessPartnerMedicalOperatorEditForm: FC<BusinessPartnerRegisterMedicalOperatorFormProps> = ({
  onSubmit,
  loading = false,
  initialValues,
  checkedDenyAccess,
  setCheckedDenyAccess,
  medicalOperatorId,
}) => {
  const { isAdministrationRole } = useRole();
  const intlMessage = useFormatMessage();
  const [checked, setChecked] = useState(true);
  const [permissionsSet, setpermissionsSet] = useState(false);
  const { isMedicalOperator } = useRole();

  const validationSchema = object().shape(
    {
      firstName: firstNameValidationSchema(intlMessage),
      lastName: lastNameValidationSchema(intlMessage),
      email: requiredEmailValidationSchema(intlMessage),
      phoneNumber: phoneNumberValidationSchema(intlMessage),
      prefixCode: phoneNumberPrefixCodeValidation(intlMessage),
      jobTitle: jobTitleValidationSchema(intlMessage),
    },
    [
      ['prefixCode', 'phoneNumber'],
      ['phoneNumber', 'prefixCode'],
    ]
  );

  const { data, isSuccess } = useQuery(
    [QueryKey.BusinessPartnerMedicalOperator],
    () => BusinessPartnersAPI.fetchUserPermissions(medicalOperatorId),
    {
      enabled: !isMedicalOperator,
    }
  );

  useEffect(() => {
    const portalsPermission =
      isSuccess &&
      data.items.find(
        (item: { permission: string; enabled: boolean }) =>
          item.permission === 'DOCTOR_PORTAL_ACCESS'
      );
    const portalAccessEnabled = portalsPermission.enabled;
    setChecked(portalAccessEnabled);
    setCheckedDenyAccess(portalAccessEnabled);
  }, [isSuccess, data, setCheckedDenyAccess]);

  //seTimeout used as a temporary solution waiting for the dedicated permissions screen in the app (set to 500 as we have to wait for setCheked() to set to the proper permission from backend)
  setTimeout(() => {
    setpermissionsSet(true);
  }, 500);

  const bpContext = useEnsureContext(BusinessPartnerContext);

  const shouldHidePartnerDropdown =
    !isNil(bpContext.parentPartnerId) || isMedicalOperator;

  const medicalOperatorEditInitialValues = {
    email: initialValues?.email,
    firstName: initialValues.firstName,
    lastName: initialValues.lastName,
    phoneNumber: initialValues.phoneNumber,
    prefixCode: initialValues.prefixCode,
    jobTitle: initialValues.jobTitle,
    partner: initialValues.partner,
    denyAccess: !checked,
  };

  return (
    <Formik
      enableReinitialize={true}
      validationSchema={validationSchema}
      initialValues={medicalOperatorEditInitialValues}
      onSubmit={onSubmit}
    >
      <fieldset disabled={!isAdministrationRole}>
        <Form {...defaultFormLayout}>
          <FormItem
            label={intlMessage({ id: 'person.firstName' })}
            name="firstName"
            required
          >
            <Input name="firstName" disabled={isMedicalOperator} />
          </FormItem>
          <FormItem
            label={intlMessage({ id: 'person.lastName' })}
            name="lastName"
            required
          >
            <Input name="lastName" disabled={isMedicalOperator} />
          </FormItem>
          <FormItem label={intlMessage({ id: 'email' })} name="email" required>
            <Input readOnly disabled name="email" />
          </FormItem>{' '}
          <FormItem
            name="phone"
            label={intlMessage({ id: 'person.phoneNumber' })}
            style={{ display: 'flex', flexDirection: 'row', width: '100%' }}
          >
            <FormItem
              name="phoneNumber"
              style={{ marginBottom: '0', display: 'flex', width: '100%' }}
            >
              <FormItem
                name="prefixCode"
                style={{
                  marginBottom: '0',
                  width: 95,
                  display: 'inline-block',
                }}
              >
                <Input
                  name="prefixCode"
                  style={{ width: 95 }}
                  addonBefore={'+'}
                  disabled={isMedicalOperator}
                />
              </FormItem>
              <Input
                style={{ width: 240, marginLeft: 10 }}
                name="phoneNumber"
                disabled={isMedicalOperator}
              />
            </FormItem>
          </FormItem>
          <FormItem
            label={intlMessage({ id: 'person.jobTitle' })}
            name="jobTitle"
          >
            <Input name="jobTitle" disabled={isMedicalOperator} />
          </FormItem>
          {!shouldHidePartnerDropdown && (
            <FormItem label={intlMessage({ id: 'partner' })} name="partner">
              <SubpartnersRegisterSelect
                defaultValue={
                  initialValues.partnerDetails
                    ? initialValues.partnerDetails.name
                    : ''
                }
                disabled
                name="partner-edit"
              />
            </FormItem>
          )}
          {!isMedicalOperator && (
            <FormItem
              name="denyAccess"
              style={{ marginLeft: '200px', width: '100%' }}
            >
              {isSuccess && permissionsSet === true && (
                <Checkbox
                  key="denyAccess"
                  name={'denyAccess'}
                  defaultChecked={!checked}
                  onChange={() => {
                    setCheckedDenyAccess &&
                      setCheckedDenyAccess(!checkedDenyAccess);
                  }}
                />
              )}
              <span style={{ marginLeft: '7px' }}>
                {' '}
                <FormattedMessage id="medicalOperators.denyAccessToApps" />
              </span>
            </FormItem>
          )}
          <AdministrationGuard>
            {!isMedicalOperator && <SaveButton loading={loading} />}
          </AdministrationGuard>
        </Form>
      </fieldset>
    </Formik>
  );
};
